import { useState } from 'react';
import { HeartIcon as HeartIconOutline } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import {
  InteractionsResponse,
  usePostInteractionsByTripByTripIdAndLinkSecretLikeMutation,
} from '../../store/backendApi';
import { useAuthenticatedUser } from '../../hooks/useAuthenticatedUser';

interface LikeButtonProps {
  tripId: string;
  storyId: string;
  interactions: InteractionsResponse | undefined;
  linkSecret: string | null;
}

export const LikeButton = ({ tripId, storyId, linkSecret, interactions }: LikeButtonProps) => {
  const { profile, isLoggedIn } = useAuthenticatedUser();

  const [showLoginNotification, setShowLoginNotification] = useState(false);
  const [postLike, { isLoading }] = usePostInteractionsByTripByTripIdAndLinkSecretLikeMutation();

  // Get likes for this story
  const story = interactions?.stories.find((st) => st.storyId === storyId);
  const likes = story?.likes || [];
  const isLiked =
    isLoggedIn && profile?.userId ? likes.some((like) => like.userId === profile.userId) : false;

  const handleLike = async () => {
    if (!isLoggedIn) {
      setShowLoginNotification(true);
      setTimeout(() => setShowLoginNotification(false), 3000);
      return;
    }

    await postLike({
      tripId,
      linkSecret: linkSecret ?? 'none',
      likeBodyRequest: {
        storyId,
      },
    });
  };

  return (
    <div className="relative flex items-center justify-center">
      <button
        onClick={handleLike}
        disabled={isLoading}
        className={`group transition-transform active:scale-90 focus:outline-none ${
          isLoading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        {isLiked ? (
          <HeartIconSolid className="w-7 h-7 text-error" />
        ) : (
          <HeartIconOutline className="w-7 h-7 text-main group-hover:text-muted" />
        )}
      </button>

      {/* Login notification */}
      {showLoginNotification && (
        <div
          className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2
        bg-neutral-darkest text-on-dark text-xs rounded shadow-lg whitespace-nowrap z-10"
        >
          Please log in to like stories
          <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-neutral-darkest rotate-45"></div>
        </div>
      )}
    </div>
  );
};
